import { useState, useEffect, useCallback } from 'react';
import { bomberosRolesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * Hook para manejar permisos del módulo de Bomberos
 * Retorna los permisos del usuario actual y helpers para verificarlos
 */
export function useBomberosPermisos() {
    const { user, isAuthenticated } = useAuth();
    const [permisos, setPermisos] = useState([]);
    const [rolNombre, setRolNombre] = useState(null);
    const [esAdmin, setEsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPermisos = async () => {
            if (!isAuthenticated) {
                setPermisos([]);
                setLoading(false);
                return;
            }

            // Superadmin y admin tienen todos los permisos
            if (user?.rol === 'superadmin' || user?.rol === 'admin') {
                setEsAdmin(true);
                setPermisos([
                    'ver_dashboard', 'ver_kpis',
                    'ver_personal', 'crear_personal', 'editar_personal', 'eliminar_personal',
                    'ver_material_mayor', 'crear_material_mayor', 'editar_material_mayor', 'eliminar_material_mayor', 'ver_checklists', 'crear_checklists',
                    'ver_epp', 'crear_epp', 'editar_epp', 'eliminar_epp', 'asignar_epp',
                    'ver_capacitaciones', 'crear_capacitaciones', 'editar_capacitaciones', 'eliminar_capacitaciones', 'registrar_asistencia', 'registrar_evaluaciones',
                    'ver_alertas', 'resolver_alertas', 'generar_alertas',
                    'ver_reportes', 'exportar_reportes',
                    'ver_companias', 'crear_companias', 'editar_companias',
                    'gestionar_roles', 'gestionar_usuarios',
                    'validar_documentos',
                ]);
                setRolNombre(user?.rol === 'superadmin' ? 'Super Administrador' : 'Administrador');
                setLoading(false);
                return;
            }

            try {
                const res = await bomberosRolesAPI.getMisPermisos();
                setPermisos(res.data.permisos || []);
                setRolNombre(res.data.rol);
                setEsAdmin(res.data.es_admin || false);
            } catch (err) {
                console.error('Error loading permisos:', err);
                setPermisos([]);
            } finally {
                setLoading(false);
            }
        };

        loadPermisos();
    }, [isAuthenticated, user]);

    /**
     * Verifica si el usuario tiene un permiso específico
     * @param {string} permiso - Código del permiso a verificar
     * @returns {boolean}
     */
    const tienePermiso = useCallback((permiso) => {
        if (esAdmin) return true;
        return permisos.includes(permiso);
    }, [permisos, esAdmin]);

    /**
     * Verifica si el usuario tiene al menos uno de los permisos especificados
     * @param {string[]} permisosRequeridos - Lista de códigos de permisos
     * @returns {boolean}
     */
    const tieneAlgunPermiso = useCallback((permisosRequeridos) => {
        if (esAdmin) return true;
        return permisosRequeridos.some(p => permisos.includes(p));
    }, [permisos, esAdmin]);

    /**
     * Verifica si el usuario tiene todos los permisos especificados
     * @param {string[]} permisosRequeridos - Lista de códigos de permisos
     * @returns {boolean}
     */
    const tieneTodosPermisos = useCallback((permisosRequeridos) => {
        if (esAdmin) return true;
        return permisosRequeridos.every(p => permisos.includes(p));
    }, [permisos, esAdmin]);

    return {
        permisos,
        rolNombre,
        esAdmin,
        loading,
        tienePermiso,
        tieneAlgunPermiso,
        tieneTodosPermisos,
    };
}

export default useBomberosPermisos;
